import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import { Input } from "../../../../components/shared/InputField";
import {
  OptionsRepricingDetails,
  RepricingGrantPreviewDetail,
} from "../../../../types/CompanyProfile";
import { formatDisplayDate } from "../../../../utils/date";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../../utils/currencyFormatter";

interface VestingRow {
  overrideKey: string;
  grantId: string;
  identifier: string;
  employeeName: string;
  projectedVestingDate: string;
  vestingType?: string;
  vestingStatus?: string;
  units?: number;
  currentStrikePrice: number;
  newStrikePrice: number;
}

function OverridePriceCellRenderer({ value: overrideKey }: { value: string }) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const [inputValue, setInputValue] = useState<string>(
    () => formik.values.vestingLevelOverrides?.[overrideKey]?.toString() ?? ""
  );
  return (
    <Input
      type="number"
      step="0.01"
      min="0"
      className="w-28 h-7 text-sm"
      placeholder="Leave blank"
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
        const current = { ...(formik.values.vestingLevelOverrides || {}) };
        if (e.target.value === "") {
          delete current[overrideKey];
        } else {
          current[overrideKey] = Number(e.target.value);
        }
        formik.setFieldValue("vestingLevelOverrides", current);
      }}
    />
  );
}

type Props = {
  previewGrants: RepricingGrantPreviewDetail[];
  onStepChange: (step?: number) => void;
  onBackClick: () => void;
  readonly?: boolean;
  isLoading?: boolean;
};

export default function VestingLevelOverrides({
  previewGrants,
  onStepChange,
  onBackClick,
  readonly,
  isLoading,
}: Props) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const currencyType = getCurrencyType();

  const vestingRows = useMemo<VestingRow[]>(
    () =>
      previewGrants.flatMap((g) =>
        (g.vestings || []).map((v: any) => ({
          overrideKey: `${g.grantId}|${
            v.projectedVestingDate ?? v.vestingDate
          }`,
          grantId: g.grantId || "",
          identifier: g.identifier,
          employeeName: g.employeeName,
          projectedVestingDate: v.projectedVestingDate ?? v.vestingDate,
          vestingType: v.vestingType,
          vestingStatus: v.state,
          units: v.projectedVestedOptions,
          currentStrikePrice: v.oldStrikePrice ?? v.currentStrikePrice,
          newStrikePrice: v.newStrikePrice,
        }))
      ),
    [previewGrants]
  );

  const overrideCount = Object.keys(
    formik.values.vestingLevelOverrides || {}
  ).length;

  const columnDefs: ColDef[] = useMemo(
    () => [
      { headerName: "Grant ID", field: "identifier", minWidth: 110 },
      { headerName: "Employee", field: "employeeName", minWidth: 150 },
      {
        headerName: "Vesting Date",
        field: "projectedVestingDate",
        minWidth: 130,
        valueFormatter: (p) => (p.value ? formatDisplayDate(p.value) : ""),
      },
      { headerName: "Status", field: "vestingStatus", minWidth: 110 },
      { headerName: "Options", field: "units", minWidth: 90 },
      {
        headerName: "Current Strike",
        field: "currentStrikePrice",
        minWidth: 140,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
      {
        headerName: "New Strike",
        field: "newStrikePrice",
        minWidth: 130,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value.toFixed(2)), currencyType || "")
            : "",
      },
      ...(!readonly
        ? [
            {
              headerName: "Override Price",
              field: "overrideKey",
              minWidth: 160,
              cellRenderer: OverridePriceCellRenderer,
            } as ColDef,
          ]
        : []),
    ],
    [currencyType, readonly]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      flex: 1,
      resizable: true,
      minWidth: 100,
      filter: true,
      menuTabs: ["filterMenuTab"],
      filterParams: { buttons: ["reset"] },
    }),
    []
  );

  const isVestingType = formik.values.repricingType === "VESTING";

  return (
    <VStack className="w-full gap-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          Vesting Level Overrides
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Optionally set a custom strike price for individual vesting tranches.
          Leave blank to use the event-level price.
        </p>
      </div>

      {!isVestingType ? (
        <VStack className="py-12 items-center gap-2 border border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-400">
            Vesting-level overrides are only available for
            <strong>Vesting Level</strong> repricing type.
          </p>
        </VStack>
      ) : vestingRows.length === 0 ? (
        <VStack className="py-12 items-center gap-2 border border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-400">
            No vesting tranches available for overrides.
          </p>
        </VStack>
      ) : (
        <VStack className="gap-2">
          {overrideCount > 0 && (
            <p className="text-xs text-orange-600 font-medium">
              {overrideCount} vesting tranche{overrideCount > 1 ? "s" : ""} with
              custom override price
            </p>
          )}
          <Box
            style={{
              height: `${Math.max(
                400,
                Math.min(vestingRows.length + 3, 13) * 60
              )}px`,
            }}
            className="w-full ag-theme-material"
          >
            <AgGridReact
              rowData={vestingRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows
              suppressRowTransform
              rowClass="border-t border-dashed hover:bg-slate-50"
              overlayNoRowsTemplate='<span style="padding:10px">No vesting tranches found</span>'
            />
          </Box>
        </VStack>
      )}

      <HStack className="justify-between mt-4">
        <ButtonPrimary1
          className="h-8 px-4 py-1"
          onClick={onBackClick}
          type="button"
        >
          Back
        </ButtonPrimary1>
        <ButtonPrimary
          className="h-8 px-4 py-1"
          onClick={() => onStepChange()}
          loading={isLoading}
          disabled={isLoading}
          type="button"
        >
          Next
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
}
